import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { useEffect, useState } from "react";
import { formMappings } from "../../../../config/formMappings";
import { getFormEntries } from "../../../../services/apiService";
import { FormEntry } from "../../../../types/types";
import InternalPage from "../../../../components/layout/internal/InternalPage";
import AverageTablesPerRegistration from "./AverageTablesPerRegistration";
import TransportationRequestTable from "./TransportationRequestTable";
import TotalTableCount from "./TotalTableCount";
import TotalTransportationRevenue from "./TotalTransportationRevenue";

// Module-level cache to prevent duplicate API calls across component instances
const transportationRequestEntriesCache = new Map<
  string,
  {
    data: FormEntry[];
    promise: Promise<FormEntry[]>;
  }
>();

export default function TransportationRequestReportPage() {
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const isVersion1 = formMapping?.version === "1";
  const hasTransportationRequest =
    formMapping && "transportationRequest" in formMapping;
  const formId = hasTransportationRequest
    ? formMapping.transportationRequest?.formId
    : undefined;
  const [v2Entries, setV2Entries] = useState<FormEntry[]>([]);

  // Make single API call for v2+ forms
  useEffect(() => {
    if (!isVersion1 && formId) {
      // Check if we have cached data or an ongoing request for this formId
      const cached = transportationRequestEntriesCache.get(formId);

      if (cached) {
        // If we have data, use it immediately
        if (cached.data.length > 0) {
          setV2Entries(cached.data);
          return;
        }

        // If there's an ongoing promise, wait for it
        if (cached.promise) {
          cached.promise
            .then((data) => {
              setV2Entries(data);
            })
            .catch(() => {
              // Error already handled by the original request
            });
          return;
        }
      }

      // Create new request and cache the promise immediately
      const promise = getFormEntries(formId)
        .then((data) => {
          // Update cache with data
          transportationRequestEntriesCache.set(formId, { data, promise });
          setV2Entries(data);
          return data;
        })
        .catch((error) => {
          console.error(
            `Error fetching form entries for form ID ${formId}:`,
            error
          );
          // Remove from cache on error so we can retry
          transportationRequestEntriesCache.delete(formId);
          throw error;
        });

      // Cache the promise immediately to prevent duplicate requests
      transportationRequestEntriesCache.set(formId, { data: [], promise });
    } else {
      // Reset entries if conditions aren't met
      setV2Entries([]);
    }
  }, [isVersion1, formId]);

  // If transportationRequest is not available for this form, show message
  if (!hasTransportationRequest) {
    return (
      <InternalPage>
        <div className="flex items-center justify-center h-[600px]">
          <div className="text-center">
            <p className="text-lg font-medium text-gray-900">
              Transportation Requests are not active for this form
            </p>
          </div>
        </div>
      </InternalPage>
    );
  }

  return (
    <InternalPage>
      <div>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-1 sm:gap-x-5 sm:gap-y-5">
          <TotalTableCount v2Entries={v2Entries} />
          <TotalTransportationRevenue v2Entries={v2Entries} />
          <AverageTablesPerRegistration v2Entries={v2Entries} />
        </dl>
        <dl className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-x-0 sm:gap-x-5 gap-y-5 h-[600px]">
          <TransportationRequestTable v2Entries={v2Entries} />
        </dl>
      </div>
    </InternalPage>
  );
}
